// Shared placed-area / persistent-zone geometry. No cast, status or movement runtime dependencies.
import { handBack } from '../janus-engine/ability/hand-back.js';
export { SHAPES } from '../janus-engine/ability/aoe/shape-ids.js';

export interface AoeOccupancy { kind: 'circle' | 'oblong'; x: number; y: number; r?: number; halfW?: number; halfD?: number; zMin: number; zMax: number }
type Point = readonly [number, number, number];

/** PHP ft_shape_aoe_intersects_target (ft3d_shape_core.php). */
export function ftShapeAoeIntersectsTarget(o: AoeOccupancy, shape: string, distance: number, depth: number, height: number,
  origin: Point, end: Point, targetCenterOnly = false): boolean {
  const tx = Number(o.x ?? 0), ty = Number(o.y ?? 0);
  const radius = targetCenterOnly ? 0 : o.kind === 'oblong' ? Math.max(Number(o.halfW ?? 0), Number(o.halfD ?? 0)) : Number(o.r ?? 0);
  if (shape === 'sphere') {
    const cx = (origin[0] + end[0]) / 2, cy = (origin[1] + end[1]) / 2, cz = (origin[2] + end[2]) / 2;
    const planarGap = Math.max(0, Math.hypot(tx - cx, ty - cy) - radius);
    const verticalGap = cz < o.zMin ? o.zMin - cz : cz > o.zMax ? cz - o.zMax : 0;
    return Math.hypot(planarGap, verticalGap) <= distance;
  }
  if (shape === 'cube') height = distance;
  const zMin = Math.min(origin[2], end[2]) - (shape === 'line' ? Math.max(0.1, height) / 2 : 0);
  const zMax = Math.max(origin[2], end[2]) + Math.max(0.1, height) * (shape === 'line' ? 0.5 : 1);
  if (o.zMax < zMin || o.zMin > zMax) return false;
  if (shape === 'circle' || shape === 'cylinder')
    return Math.hypot(tx - (origin[0] + end[0]) / 2, ty - (origin[1] + end[1]) / 2) <= distance + radius;
  if (shape === 'cube' || shape === 'square') {
    const cx = (origin[0] + end[0]) / 2, cy = (origin[1] + end[1]) / 2, half = distance / 2;
    const ax = end[0] - origin[0], ay = end[1] - origin[1], axisLength = Math.hypot(ax, ay);
    let lx = tx - cx, ly = ty - cy;
    if (axisLength > 1e-6) { const ux = ax / axisLength, uy = ay / axisLength; [lx, ly] = [lx * ux + ly * uy, -lx * uy + ly * ux]; }
    return Math.abs(lx) <= half + radius && Math.abs(ly) <= half + radius;
  }
  const sx = end[0] - origin[0], sy = end[1] - origin[1], lengthSq = sx * sx + sy * sy;
  const vx = tx - origin[0], vy = ty - origin[1];
  if (shape === 'line') {
    const t = lengthSq > 1e-9 ? Math.max(0, Math.min(1, (vx * sx + vy * sy) / lengthSq)) : 0;
    return Math.hypot(tx - (origin[0] + sx * t), ty - (origin[1] + sy * t)) <= Math.max(0.5, depth / 2) + radius;
  }
  if (shape === 'cone') {
    const forward = Math.sqrt(lengthSq), targetDistance = Math.hypot(vx, vy);
    if (targetDistance > distance + radius) return false;
    if (forward <= 1e-9 || targetDistance <= 1e-9) return true;
    return (vx * sx + vy * sy) / (forward * targetDistance) >= Math.cos(Math.PI / 4);
  }
  handBack('ft_shape_aoe_intersects_target:unknown-shape');
}
